import React, { useEffect, useState } from 'react';
import CloseIcon from '../icons/CloseIcon';
import InputField from './InputField';
import TextAreaField from './TextAreaField';
import Button from './Button';
import DropdownButton from './DropdownButton';

const pxToRem = (value: number) => `${value / 16}rem`;

export interface StampTemplateFormData {
  title: string;
  description: string;
  stampCount: number;
  gradeFilter: string;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: StampTemplateFormData) => Promise<void> | void;
  initialData?: StampTemplateFormData | null;
  mode?: 'create' | 'edit';
}

const gradeOptions = [
  { value: 'all', label: '전체 학년' },
  { value: '1', label: '1학년' },
  { value: '2', label: '2학년' },
  { value: '3', label: '3학년' },
];

const AddStampTemplateDialog: React.FC<Props> = ({
  isOpen,
  onClose,
  onSave,
  initialData,
  mode = 'create',
}) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [stampCount, setStampCount] = useState('1');
  const [gradeFilter, setGradeFilter] = useState('all');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setTitle('');
      setDescription('');
      setStampCount('1');
      setGradeFilter('all');
      setIsSubmitting(false);
      return;
    }
    if (initialData) {
      setTitle(initialData.title);
      setDescription(initialData.description);
      setStampCount(String(initialData.stampCount));
      setGradeFilter(initialData.gradeFilter || 'all');
    }
  }, [isOpen, initialData]);

  const handleSave = async () => {
    if (!title.trim() || !description.trim() || !stampCount.trim()) return;
    const count = parseInt(stampCount, 10);
    if (isNaN(count) || count <= 0) return;

    setIsSubmitting(true);
    try {
      await onSave({
        title: title.trim(),
        description: description.trim(),
        stampCount: count,
        gradeFilter,
      });
      onClose();
    } catch (error) {
      console.error('도장 템플릿 저장 실패:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const isSaveDisabled =
    !title.trim() || !description.trim() || !stampCount.trim() || isSubmitting;

  if (!isOpen) return null;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 1000,
        fontFamily: 'Pretendard, sans-serif',
      }}
      onClick={onClose}
    >
      <div
        style={{
          width: pxToRem(450),
          background: 'var(--Color-background-normal, white)',
          borderRadius: pxToRem(16),
          boxShadow: '0px 0px 3px 0px rgba(0, 0, 0, 0.06), 0px 8px 16px 0px rgba(0, 0, 0, 0.12)',
          padding: `${pxToRem(24)} ${pxToRem(28)}`,
          display: 'flex',
          flexDirection: 'column',
          gap: pxToRem(40),
          maxHeight: '90vh',
          overflow: 'auto',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: 'flex', gap: pxToRem(8), width: '100%' }}>
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: pxToRem(2) }}>
            <div
              style={{
                color: 'var(--Color-text-normal, #161616)',
                fontSize: pxToRem(20),
                fontWeight: 600,
                lineHeight: pxToRem(30),
                letterSpacing: '-0.02em',
              }}
            >
              {mode === 'edit' ? '도장 템플릿 수정하기' : '도장 템플릿 추가하기'}
            </div>
            <div
              style={{
                color: 'var(--Color-text-assistive, #989898)',
                fontSize: pxToRem(16),
                fontWeight: 500,
                lineHeight: pxToRem(24),
              }}
            >
              템플릿을 만들어 학생에게 도장을 지급해 보세요.
            </div>
          </div>
          <div
            onClick={onClose}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClose();
              }
            }}
            aria-label="닫기"
            style={{
              padding: pxToRem(4),
              borderRadius: pxToRem(8),
              cursor: 'pointer',
              flexShrink: 0,
            }}
          >
            <CloseIcon width={24} height={24} color="var(--Color-text-normal, #161616)" />
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: pxToRem(24), width: '100%' }}>
          <InputField
            value={title}
            onChange={setTitle}
            placeholder="템플릿 제목을 입력해 주세요"
            width="100%"
            height={pxToRem(48)}
            size="small"
          />
          <TextAreaField
            value={description}
            onChange={setDescription}
            placeholder="템플릿 설명을 입력해 주세요"
            width="100%"
            height={pxToRem(84)}
            size="small"
          />
          <div style={{ display: 'flex', flexDirection: 'column', gap: pxToRem(8) }}>
            <div
              style={{
                color: 'var(--Color-text-assistive, #989898)',
                fontSize: pxToRem(13),
                fontWeight: 500,
              }}
            >
              지급 대상 학년
            </div>
            <DropdownButton
              options={gradeOptions}
              value={gradeFilter}
              onChange={setGradeFilter}
              size="medium"
              width="100%"
              height="40px"
            />
          </div>
          <InputField
            value={stampCount}
            onChange={setStampCount}
            placeholder="지급할 도장 개수"
            type="number"
            width="100%"
            height={pxToRem(48)}
            size="small"
          />
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: pxToRem(8) }}>
          <Button variant="cancel" size="medium" onClick={onClose} disabled={isSubmitting}>
            취소
          </Button>
          <Button
            variant="submit"
            size="medium"
            onClick={handleSave}
            disabled={isSaveDisabled}
          >
            {isSubmitting ? '저장 중...' : mode === 'edit' ? '수정하기' : '추가하기'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AddStampTemplateDialog;
